/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css } from 'aphrodite';
import { useHistory } from 'react-router';
import { colors } from '@blend/consumer-components';

import { ContextualHelpList } from '../../components/form/ContextualHelpList/ContextualHelpList';
import {
  contextualHelpRoomInstructions,
  contextualHelpClosedCaptionSupported,
} from '../../components/form/ContextualHelpList/contextualHelpUtils';
import { Message } from '../../components/form/TaskMessages/messageTypes';
import { TaskMessages } from '../../components/form/TaskMessages/TaskMessages';

import { styles as taskStyles } from '../../components/form/styles';
import { useRoom } from '../../hooks/useRoom';
import { useConsumerTheme, ConsumerAppConfigContext } from '../../hooks/useConsumerTheme';
import { participantAPI } from '../../api/participant';
import { Footer } from '../TasksPage/Footer/Footer';
import { ActionButton } from '../../components/ActionButton/ActionButton';
import { RouterStates } from '../../definitions/RouterStates';
import { formatScheduleDateAtTime } from '@utils/formatting';
import { getConfig } from '@utils/configs';

const generateStyles = (_appConfig: ConsumerAppConfigContext) => ({
  pageWrapper: {
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
  },
  testDeviceButton: {
    marginTop: 24,
  },
  ...taskStyles,
});

export namespace ParticipantRoomEarlyPage {
  export interface Props {}
}

// Currently we have two ways of creating task template, one is from the client and the other is from
// the server task templates. Ideally, this should be consistence and every task should be created
// from the backend. However, the current task engine requires a session to exist when creating a task.
// This task here is shown when participant are too early to join the room (no session is created yet).
// Thus, as a workaround, creating task within the frontend component here.
// TODO: Refactor this and have all the tasks live in backend
const getParticipantEarlyTemplate = (scheduledInfo: string) => {
  const messages: Message[] = [
    {
      display: `You're a little early for your online notarization.`,
      type: 'simple',
      headingType: 'primaryHeading',
    },
    {
      display: 'Alarm clock',
      type: 'image',
      image: 'alarm-clock',
    },
    {
      display: `Your online notarization is scheduled for <strong>${scheduledInfo}</strong>.`,
      type: 'simple',
      headingType: 'secondaryHeading',
    },
    {
      display: 'While you wait, you can test your device to ensure everything is working properly.',
      type: 'simple',
      headingType: 'secondaryHeading',
    },
  ];
  const contextualHelps = [contextualHelpRoomInstructions];

  if (getConfig()?.enableZoomForBSR20240730) {
    contextualHelps.unshift(contextualHelpClosedCaptionSupported);
  }

  return {
    messages,
    contextualHelps,
  };
};

export const ParticipantRoomEarlyPage: React.FC<ParticipantRoomEarlyPage.Props> = ({}) => {
  const { roomId } = useRoom();
  const { styles } = useConsumerTheme(generateStyles);
  const [scheduledTime, setScheduledTime] = useState('');
  const history = useHistory();

  useEffect(() => {
    (async () => {
      const roomDetails = await participantAPI.rooms.fetchRoomDetail(roomId);
      const formattedScheduledTime = formatScheduleDateAtTime(roomDetails.scheduleDate, roomDetails?.scheduleTime);
      setScheduledTime(formattedScheduledTime);
    })();
  }, [roomId]);

  const handleTestDevice = () => {
    history.push(RouterStates.DEVICE_TESTING.replace(':roomId', roomId));
  };

  const template = getParticipantEarlyTemplate(scheduledTime);

  return (
    <div className={css(styles.pageWrapper)} data-testid="ParticipantRoomEarlyPage">
      <div className={css(styles.defaultTemplate)}>
        <div>
          <TaskMessages messages={template.messages} />
          <ContextualHelpList contextualHelps={template.contextualHelps} />
          <div className={css(styles.testDeviceButton)}>
            <ActionButton
              data-dd-privacy="allow"
              color={colors.catalyst}
              onClick={handleTestDevice}
              data-testid="TestDeviceButton"
            >
              Test your device
            </ActionButton>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
